import { createTheme } from "@mui/material/styles";
import hexToRgba from "utils/hexToRgba";
import { RGB_Log_Shade } from "utils/utils";

/**
 * Creates a Material-UI theme based on primary and secondary colors
 *
 * @param {string} mainColor - The primary color in hex format (e.g., '#6699cc')
 * @param {string} secondaryColor - The secondary color in hex format (e.g., '#203b0e')
 * @returns {Theme} Material-UI theme object
 *
 * Features:
 * - Custom color palette with opaque variants
 * - Styled tooltips with inverted colors and shading
 * - Custom input base styling with disabled state
 */
export function createAppTheme(mainColor, secondaryColor) {
  // Create opaque variants of the colors for transparency effects
  const mainOpaqueColor = hexToRgba(mainColor, 0.5);
  const secondaryOpaqueColor = hexToRgba(secondaryColor, 0.5);

  return createTheme({
    spacing: 8,
    palette: {
      primary: {
        main: mainColor,
        mainOpaque: mainOpaqueColor,
      },
      secondary: {
        main: secondaryColor,
        mainOpaque: secondaryOpaqueColor,
      },
    },
    components: {
      // Custom tooltip styling with color shading
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontSize: "0.8em",
            color: RGB_Log_Shade(-0.7, hexToRgba(mainColor)),
            backgroundColor: RGB_Log_Shade(0.7, hexToRgba(secondaryColor)),
          },
        },
      },
      // Custom input base styling
      MuiInputBase: {
        styleOverrides: {
          root: {
            backgroundColor: "white",
            "&.Mui-disabled": {
              backgroundColor: "#f5f5f5",
            },
          },
        },
      },
      // Phones: Apple/Google minimum touch target. Desktop is unchanged.
      MuiIconButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            [theme.breakpoints.down("md")]: {
              minWidth: 44,
              minHeight: 44,
              // A text field's adornment (e.g. the password visibility
              // toggle) is clamped to the input height; keep it at its size.
              ".MuiInputAdornment-root &": { minWidth: 0, minHeight: 0 },
              // Autocomplete clear/popup indicators sit inside the input too.
              ".MuiAutocomplete-endAdornment &": { minWidth: 0, minHeight: 0 },
              // size="small" marks dense rows (legend, tables); 44px would
              // blow up their layout, so they keep their compact size.
              "&.MuiIconButton-sizeSmall": { minWidth: 0, minHeight: 0 },
            },
          }),
        },
      },
      // Phones: dialogs never extend past the dynamic viewport (address bar,
      // keyboard), and scroll inside instead. Full-screen dialogs stay edge
      // to edge.
      MuiDialog: {
        styleOverrides: {
          paper: ({ theme }) => ({
            [theme.breakpoints.down("md")]: {
              margin: 16,
              maxHeight: "calc(var(--app-vh) - 32px)",
              "&.MuiDialog-paperFullScreen": { margin: 0, maxHeight: "none" },
            },
          }),
        },
      },
    },
  });
}
